import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FundoMobile, COLORS } from "@/components/DesafioFundo";
import useBarlow from "@/components/useBarlow";

const BASE = process.env.REACT_APP_BACKEND_URL;
const AVATARES = ["gato","cachorro","panda","raposa","coruja","pinguim","leao","coelho","coala","sapo","polvo","unicornio"];
const AV_URL = (a) => `/desafio/avatares/${a}.png`;
const LOGO = "/desafio/logo-mpires-branco.png";
const CONECTA = "/mascote/conecta-3d.png";
const CORS = ["#117DBB","#D98B20","#B23A3A","#2E8B4D"];
const SHAPES = ["●","▲","■","◆"];
const KEY = (pin) => `desafio_player_${pin}`;
const VOO_CSS = `@keyframes voo{0%,100%{transform:perspective(700px) translate(0,0) rotateY(0) rotateZ(0)}25%{transform:perspective(700px) translate(7px,-6px) rotateY(14deg) rotateZ(2deg)}50%{transform:perspective(700px) translate(0,-10px)}75%{transform:perspective(700px) translate(-7px,-5px) rotateY(-14deg) rotateZ(-2deg)}}.conecta{animation:voo 5.2s ease-in-out infinite}`;
const BRANDBG = COLORS.mobileBg;

function TopoNavy({ pin, children }) {
  // Topo navy com círculos (atrás) + logo branco + selo + sala + Conecta
  return (
    <div style={{ background: COLORS.navy, color: "#fff", padding: "24px 16px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <FundoMobile />
      <div style={{ position: "relative", zIndex: 2 }}>
        <img src={LOGO} alt="MPires" style={{ height: 32, display: "block", margin: "0 auto" }} />
        <div style={{ marginTop: 10, fontSize: 22, fontWeight: 800 }}>Desafio</div>
        <div style={{ marginTop: 12 }}>
          <span style={{ display: "inline-block", padding: "6px 16px", borderRadius: 999, border: "1.5px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.08)", fontSize: 13, color: COLORS.textSec }}>Limpeza e Desinfecção Hospitalar</span>
        </div>
        {pin && <div style={{ marginTop: 10, color: COLORS.accent, fontSize: 12, fontWeight: 700 }}>● Sala {pin.replace(/(\d{3})(\d{3})/, "$1 $2")} · ao vivo</div>}
        <div style={{ marginTop: 16 }}>
          <img src={CONECTA} alt="" className="conecta" style={{ width: 160 }} />
        </div>
        {children}
      </div>
    </div>
  );
}

function Container({ children, reconectando }) {
  return (
    <div style={{ minHeight: "100vh", background: COLORS.navy, display: "flex", justifyContent: "center", alignItems: "stretch", fontFamily: "'Barlow',sans-serif", color: "#003E62" }}>
      <style>{VOO_CSS}</style>
      <div style={{ width: "100%", maxWidth: 480, background: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
        {reconectando && (
          <div data-testid="banner-reconectando" style={{ position: "fixed", top: 0, left: 0, right: 0, background: "rgba(0,62,98,0.95)", color: "#fff", textAlign: "center", fontSize: 13, fontWeight: 700, padding: "6px 10px", zIndex: 50, fontFamily: "'Barlow',sans-serif" }}>
            Reconectando…
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function DesafioParticipante() {
  useBarlow();
  const { pin: pinParam } = useParams();
  const nav = useNavigate();
  const [pin, setPin] = useState(pinParam || "");
  const [token, setToken] = useState("");
  const [nome, setNome] = useState("");
  const [avatar, setAvatar] = useState("gato");
  const [state, setState] = useState(null);
  const [prevStatus, setPrevStatus] = useState("");
  const [pontosExibidos, setPontosExibidos] = useState(0);
  const [entrando, setEntrando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [reconectando, setReconectando] = useState(false);
  const [entrarErro, setEntrarErro] = useState("");
  const stopRef = useRef(false);
  const backoffRef = useRef(2000);
  const timeoutRef = useRef(null);
  const retryRef = useRef({ running: false });
  const stateRef = useRef(null);

  useEffect(() => {
    if (pinParam) {
      const saved = sessionStorage.getItem(KEY(pinParam));
      if (saved) { const o = JSON.parse(saved); setToken(o.token); setNome(o.nome); setAvatar(o.avatar); }
    }
  }, [pinParam]);

  // DES-1.3: polling 2s ± 300ms, pausa com aba oculta, backoff 2/4/8s em falha.
  useEffect(() => {
    if (!pinParam || !token) return;
    stopRef.current = false;
    backoffRef.current = 2000;
    let errou = false;
    const tick = async () => {
      if (stopRef.current) return;
      if (document.hidden) {
        timeoutRef.current = setTimeout(tick, 1000);
        return;
      }
      try {
        const { data } = await axios.get(`${BASE}/api/desafio/public/salas/${pinParam}`, { headers: { "X-Player-Token": token }, timeout: 8000 });
        setReconectando(false);
        backoffRef.current = 2000;
        errou = false;
        stateRef.current = data;
        setState((prev) => {
          if (data.status !== "question") setPontosExibidos(data.pontos_total || 0);
          setPrevStatus(prev?.status || "");
          return data;
        });
      } catch (e) {
        if (e?.response?.status === 404) {
          sessionStorage.removeItem(KEY(pinParam));
          setToken(""); setState(null); stopRef.current = true; return;
        }
        errou = true;
        setReconectando(true);
        backoffRef.current = Math.min(backoffRef.current * 2, 8000);
      }
      if (stopRef.current) return;
      const jitter = Math.round((Math.random() - 0.5) * 600);
      const next = errou ? backoffRef.current : (2000 + jitter);
      timeoutRef.current = setTimeout(tick, Math.max(500, next));
    };
    tick();
    const onVis = () => { if (!document.hidden && !stopRef.current) { clearTimeout(timeoutRef.current); tick(); } };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopRef.current = true;
      clearTimeout(timeoutRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinParam, token]);

  const entrarPIN = () => { if (/^\d{6}$/.test(pin)) nav(`/desafio/${pin}`); };

  const entrarSala = async () => {
    if (!nome.trim() || !avatar) return;
    setEntrando(true); setEntrarErro("");
    try {
      const { data } = await axios.post(`${BASE}/api/desafio/public/salas/${pinParam}/entrar`, { nome: nome.trim(), avatar }, { timeout: 10000 });
      setToken(data.token);
      sessionStorage.setItem(KEY(pinParam), JSON.stringify({ token: data.token, nome: data.nome, avatar: data.avatar }));
    } catch (e) {
      setEntrarErro(e?.response?.data?.detail || "Falha de rede. Verifique o Wi-Fi e tente de novo.");
    }
    finally { setEntrando(false); }
  };

  // DES-1.3: envio de resposta com retry automático até o fim da pergunta.
  const responder = async (alt) => {
    if (enviando) return;
    if (state?.meu_alt !== null && state?.meu_alt !== undefined) return;
    setEnviando(true);
    retryRef.current = { running: true };
    let wait = 400;
    while (retryRef.current.running) {
      const s = stateRef.current;
      if (s && s.status && s.status !== "question") break;
      try {
        await axios.post(`${BASE}/api/desafio/public/salas/${pinParam}/responder`, { alt }, { headers: { "X-Player-Token": token }, timeout: 6000 });
        setReconectando(false);
        break;
      } catch (e) {
        const code = e?.response?.status;
        if (code === 409) break;
        if (code === 400 || code === 403) break;
        setReconectando(true);
        await new Promise((r) => setTimeout(r, wait));
        wait = Math.min(wait * 2, 2000);
      }
    }
    retryRef.current.running = false;
    setEnviando(false);
  };

  // ========= PIN screen =========
  if (!pinParam) {
    return (
      <Container reconectando={reconectando}>
        <TopoNavy pin="">
          <div style={{ background: "#fff", borderRadius: 20, padding: 20, color: "#003E62", margin: "16px 16px 0", boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
            <div style={{ color: "#003E62", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Digite o PIN da sala</div>
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="000000" data-testid="pin-input"
              style={{ width: "100%", fontSize: 36, padding: 18, borderRadius: 12, border: "1.5px solid rgba(0,62,98,0.28)", textAlign: "center", letterSpacing: 10, boxSizing: "border-box", fontWeight: 700 }} />
            <button onClick={entrarPIN} disabled={!/^\d{6}$/.test(pin)} data-testid="btn-entrar-pin"
              style={{ marginTop: 16, width: "100%", padding: 14, fontSize: 18, fontWeight: 800, background: "#003E62", color: "#fff", border: 0, borderRadius: 12, cursor: "pointer", opacity: /^\d{6}$/.test(pin) ? 1 : 0.5 }}>
              Entrar
            </button>
          </div>
        </TopoNavy>
      </Container>
    );
  }

  // ========= Entry (nome + avatar) =========
  if (!token) {
    return (
      <Container reconectando={reconectando}>
        <TopoNavy pin={pinParam} />
        <div style={{ background: "#fff", borderRadius: 20, padding: 20, color: "#003E62", margin: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
          <label style={{ fontSize: 14, fontWeight: 700, color: "#003E62" }}>Como você quer aparecer no ranking?</label>
          <input value={nome} onChange={(e) => setNome(e.target.value.slice(0,16))} placeholder="Seu nome" data-testid="part-nome"
            style={{ width: "100%", padding: 12, fontSize: 16, borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", marginTop: 6, boxSizing: "border-box" }} />
          <div style={{ marginTop: 14, fontSize: 14, fontWeight: 700, color: "#003E62" }}>Escolha seu avatar</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginTop: 8 }}>
            {AVATARES.map((a) => (
              <button key={a} onClick={() => setAvatar(a)} data-testid={`part-avatar-${a}`}
                style={{ padding: 4, borderRadius: 12, border: avatar === a ? "2.5px solid #117DBB" : "2px solid transparent", background: avatar === a ? "rgba(17,125,187,0.08)" : "transparent", cursor: "pointer" }}>
                <img src={AV_URL(a)} alt={a} style={{ width: "100%", display: "block" }} />
              </button>
            ))}
          </div>
          <button onClick={entrarSala} disabled={!nome.trim() || entrando} data-testid="part-entrar"
            style={{ marginTop: 16, width: "100%", padding: 14, fontSize: 17, fontWeight: 800, background: "#003E62", color: "#fff", border: 0, borderRadius: 12, cursor: "pointer", opacity: (!nome.trim() || entrando) ? 0.5 : 1 }}>
            Entrar no desafio →
          </button>
          {entrarErro && <p data-testid="part-entrar-erro" style={{ textAlign: "center", color: "#B23A3A", fontSize: 13, marginTop: 10, fontWeight: 600 }}>{entrarErro}</p>}
          <p style={{ textAlign: "center", color: "#4A6478", fontSize: 12, marginTop: 10 }}>Sem cadastro e sem baixar aplicativo</p>
        </div>
      </Container>
    );
  }

  // ========= Dentro =========
  if (!state) return <Container reconectando={reconectando}><div style={{ padding: 40, textAlign: "center", color: "#4A6478" }}>Conectando…</div></Container>;
  const S = state.status;
  const perg = state.pergunta;
  const jogador = state.jogador || { avatar };

  // Barra topo com placar — mas só mostra pontos reais quando status != question.
  const pontosHeader = (S === "question") ? pontosExibidos : (state.pontos_total || 0);
  const topBar = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#fff", borderBottom: "1px solid rgba(0,62,98,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: "#003E62", fontWeight: 800 }}>Desafio</span>
        <img src="/marca/mpires-connect-cor-petroleo.png" alt="MPires" style={{ height: 22 }} />
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#003E62", color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
        <img src={AV_URL(jogador.avatar)} alt="" style={{ width: 22, height: 22, borderRadius: "50%" }} /> {pontosHeader} pts
      </div>
    </div>
  );

  // Espera (waiting / rank): topo navy + mensagem destaque
  if (S === "waiting" || S === "rank") {
    const msg = S === "waiting" ? "Você está dentro" : "Preparando a próxima…";
    return (
      <Container reconectando={reconectando}>
        {topBar}
        <div style={{ flex: 1, background: COLORS.mobileBg, color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <FundoMobile />
          <div style={{ position: "relative", zIndex: 2, width: "100%" }}>
          <img src={CONECTA} alt="" className="conecta" style={{ width: 180 }} />
          <h2 style={{ margin: "20px 0 4px", fontSize: 28, fontWeight: 800 }}>{msg}</h2>
          {S === "rank" ? <p style={{ color: COLORS.textSec }}>Aguarde a próxima pergunta…</p> : <p style={{ color: COLORS.textSec }}>Aguarde o instrutor começar o desafio.</p>}
          {state.minha_pos && (
            <div style={{ marginTop: 20, background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.18)", padding: "10px 20px", borderRadius: 12, fontSize: 16, display: "inline-block" }}>
              Você está em <strong>{state.minha_pos}º lugar</strong>
            </div>
          )}
          </div>
        </div>
      </Container>
    );
  }

  // Pergunta: 4 botões GRANDES preenchendo a tela
  if (S === "question" && perg) {
    const bloq = state.meu_alt !== null && state.meu_alt !== undefined;
    return (
      <Container reconectando={reconectando}>
        {topBar}
        <div style={{ padding: "12px 14px 14px", background: "#fff", borderBottom: "1px solid rgba(0,62,98,0.08)" }}>
          <div style={{ fontSize: 12, color: "#4A6478", fontWeight: 700 }}>Pergunta {state.pergunta_atual + 1} de {state.total_perguntas}</div>
          <div style={{ color: "#003E62", fontSize: 16, fontWeight: 700, marginTop: 4 }}>{perg.pergunta}</div>
          {bloq && <div data-testid="part-resp-enviada" style={{ marginTop: 8, color: "#2E8B4D", fontSize: 13, fontWeight: 700 }}>✓ Resposta enviada</div>}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, padding: 14 }}>
          {perg.alternativas.map((a, i) => {
            const marcada = state.meu_alt === i;
            return (
              <button key={i} onClick={() => responder(i)} disabled={bloq || enviando} data-testid={`part-alt-${i}`}
                style={{ flex: 1, background: CORS[i], color: "#fff", border: 0, borderRadius: 16, padding: "16px 18px", fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 12, textAlign: "left", opacity: bloq && !marcada ? 0.4 : 1, cursor: bloq ? "default" : "pointer", boxShadow: marcada ? "0 0 0 3px rgba(0,0,0,0.25)" : "none", minHeight: 100 }}>
                <span style={{ width: 42, height: 42, borderRadius: "50%", background: "rgba(255,255,255,0.25)", display: "inline-grid", placeItems: "center", fontSize: 20, flexShrink: 0 }}>{SHAPES[i]}</span>
                <span style={{ flex: 1 }}>{a}</span>
              </button>
            );
          })}
        </div>
      </Container>
    );
  }

  // Resposta: acerto / erro / tempo esgotado
  if (S === "answer") {
    const semResposta = state.meu_alt === null || state.meu_alt === undefined;
    const acertou = state.meus_pontos_q > 0;
    const corTopo = acertou ? "#2E8B4D" : "#B23A3A";
    const icone = semResposta ? "⏱" : (acertou ? "✓" : "✕");
    const titulo = semResposta ? "O tempo acabou" : (acertou ? "Resposta certa!" : "Não foi dessa vez");
    return (
      <Container reconectando={reconectando}>
        {topBar}
        <div style={{ flex: 1, background: COLORS.mobileBg, color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <FundoMobile />
          <div style={{ position: "relative", zIndex: 2, width: "100%" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", background: corTopo, display: "grid", placeItems: "center", fontSize: 60, marginBottom: 14, marginLeft: "auto", marginRight: "auto" }}>{icone}</div>
          <div style={{ marginBottom: 10 }}>
            <img src={AV_URL(jogador.avatar)} alt="" style={{ width: 100, borderRadius: 20 }} />
          </div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800 }}>{titulo}</h2>
          {acertou && <div style={{ fontSize: 42, fontWeight: 800, marginTop: 8 }}>+{state.meus_pontos_q}</div>}
          {state.minha_pos && (
            <div style={{ marginTop: 16, background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.18)", padding: "10px 20px", borderRadius: 12, fontSize: 16, display: "inline-block" }}>
              Você está em <strong>{state.minha_pos}º lugar</strong>
            </div>
          )}
          </div>
        </div>
      </Container>
    );
  }

  if (S === "podium") {
    return (
      <Container reconectando={reconectando}>
        {topBar}
        <div style={{ flex: 1, background: COLORS.mobileBg, color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <FundoMobile />
          <div style={{ position: "relative", zIndex: 2 }}>
          <img src={CONECTA} alt="" className="conecta" style={{ width: 180 }} />
          <h2 style={{ margin: "20px 0 4px", fontSize: 28, fontWeight: 800 }}>Você terminou em {state.minha_pos}º lugar</h2>
          <p style={{ color: COLORS.textSec }}>{state.pontos_total} pontos. Obrigado por participar!</p>
          </div>
        </div>
      </Container>
    );
  }

  return <Container reconectando={reconectando}><div style={{ padding: 40 }}>…</div></Container>;
}
